import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '@/hooks/useAuth'
import { TISACard } from '@/components/tisa/TISACard'
import { TISAButton } from '@/components/tisa/TISAButton'
import { StudentLoginForm } from '@/components/auth/StudentLoginForm'
import { LogIn, Mail, Lock } from 'lucide-react'
import { toast } from '@/hooks/use-toast'

export default function LoginPage() {
  const navigate = useNavigate()
  const { signInWithEmail } = useAuth()

  //  Login mode state
  const [loginMode, setLoginMode] = useState<'email' | 'pin'>('pin')
  const [isLoading, setIsLoading] = useState(false)

  // Email/Password form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email || !password) {
      toast({
        title: 'Missing information',
        description: 'Please enter both email and password',
        variant: 'destructive'
      })
      return
    }

    setIsLoading(true)

    try {
      const user = await signInWithEmail(email, password)

      toast({
        title: 'Welcome back!',
        description: `Logged in as ${user.name}`,
      })

      // Navigate based on role
      if (user.role === 'student') {
        navigate('/student/dashboard')
      } else if (user.role === 'teacher') {
        navigate('/teacher/dashboard')
      } else if (user.role === 'librarian') {
        navigate('/librarian/dashboard')
      } else {
        navigate('/student/dashboard')
      }
    } catch (error) {
      toast({
        title: 'Login failed',
        description: error instanceof Error ? error.message : 'Invalid email or password',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-tisa-blue-50 to-tisa-purple-50 p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-4">
          {/* TISA Logo */}
          <div className="flex justify-center">
            <img
              src="/images/tisa-logo.png"
              alt="TISA Library"
              className="h-20 w-auto object-contain"
            />
          </div>

          <h1 className="text-child-4xl font-bold text-primary">
            TISAverse
          </h1>
          <p className="text-child-lg text-muted-foreground">
            Welcome back! Please log in to continue.
          </p>
        </div>

        {/* Login Mode Toggle */}
        <div className="flex gap-2">
          <TISAButton
            variant={loginMode === 'pin' ? 'theme-blue' : 'outline'}
            className="flex-1"
            onClick={() => setLoginMode('pin')}
          >
            🎒 Student PIN
          </TISAButton>
          <TISAButton
            variant={loginMode === 'email' ? 'theme-green' : 'outline'}
            className="flex-1"
            onClick={() => setLoginMode('email')}
            icon={<Mail className="h-4 w-4" />}
          >
            Staff Login
          </TISAButton>
        </div>

        {/* Login Forms */}
        {loginMode === 'pin' ? (
          <StudentLoginForm onBack={() => setLoginMode('email')} />
        ) : (
          <TISACard size="lg" className="p-8">
            <form onSubmit={handleEmailLogin} className="space-y-6">
              <div className="text-center space-y-2">
                <div className="text-4xl">👨‍🏫</div>
                <h2 className="text-child-xl font-bold">Staff Login</h2>
                <p className="text-child-sm text-muted-foreground">
                  Teachers and Librarians
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-child-sm font-medium">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="teacher@tisa.school"
                      className="w-full pl-10 pr-4 py-3 rounded-lg border-2 border-input bg-background text-child-base focus:border-primary focus:outline-none"
                      disabled={isLoading}
                      autoFocus
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="text-child-sm font-medium">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-4 py-3 rounded-lg border-2 border-input bg-background text-child-base focus:border-primary focus:outline-none"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <TISAButton
                type="submit"
                variant="theme-green"
                size="lg"
                className="w-full"
                disabled={isLoading || !email || !password}
                isLoading={isLoading}
                icon={!isLoading ? <LogIn className="h-5 w-5" /> : undefined}
              >
                {isLoading ? 'Logging in...' : 'Login with Email'}
              </TISAButton>
            </form>
          </TISACard>
        )}
      </div>
    </div>
  )
}